import { SelectedZoneOrWorkplace } from "../../../../features/Reports/slices/report.slice";
import {
  CapacityObject,
  MapMarkerLocation
} from "../../../../features/Reports/typings/reports.types";
import { SingleSuperCluster } from "../hooks/useSupercluster";

/**
 * Calculates the average latitude of all locations to center the map.
 * If no locations are present, a default value for Germany (approx. center) is returned.
 */
export const latitudeLocation = (mapMarkerLocations: MapMarkerLocation[] | undefined) =>
  mapMarkerLocations?.length
    ? mapMarkerLocations.reduce((acc, loc) => acc + (loc.geoInformation?.[0]?.latitude ?? 0), 0) /
      mapMarkerLocations.length
    : 51;

/**
 * Calculates the average longitude of all locations to center the map.
 * If no locations are present, a default value for Germany (approx. center) is returned.
 */
export const longitudeLocation = (mapMarkerLocations: MapMarkerLocation[] | undefined) =>
  mapMarkerLocations?.length
    ? mapMarkerLocations.reduce((acc, loc) => acc + (loc.geoInformation?.[0]?.longitude ?? 0), 0) /
      mapMarkerLocations.length
    : 10;

/**
 * Determines the initial zoom level of the map based on the number of locations.
 * If there are no locations, a Germany overview is shown; for one location, a city view is shown.
 * For multiple locations, an initial value is set, which is then dynamically adjusted by `fitBounds`.
 */
export const zoomLocation = (mapMarkerLocations: MapMarkerLocation[] | undefined) => {
  const count = mapMarkerLocations?.length ?? 0;

  if (count === 0) return 6.5;
  if (count === 1) return 13;
  return 6.5;
};

export const currentEntityData = (
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace,
  capacitySelectedWorkplaceOrZone: CapacityObject | undefined,
  capacitySelectedByAttFilter: CapacityObject[] | undefined,
  currentFloors: { id: number; open: boolean }[],
  capacitySelectedFloors?: CapacityObject[]
) => {
  if (selectedZoneOrWorkplace?.open) {
    return [capacitySelectedWorkplaceOrZone];
  }
  return capacitySelectedFloors?.filter(floor =>
    currentFloors.some(activeFloor => activeFloor.id === floor.id)
  );
};

export function compareClustersCoordinates(superClusterClusters: SingleSuperCluster[]) {
  if (superClusterClusters.some(cluster => cluster.properties.cluster === false)) {
    const newClu = superClusterClusters.map((cluster, index) => {
      // skip the comparison for the first cluster(index = 0)
      if (!index) return cluster;

      const isSameLongitudeAsPreviousCluster =
        cluster.geometry.coordinates[0] === superClusterClusters[0].geometry.coordinates[0];
      const isSameLatitudeAsPreviousCluster =
        cluster.geometry.coordinates[1] === superClusterClusters[0].geometry.coordinates[1];

      if (isSameLongitudeAsPreviousCluster && isSameLatitudeAsPreviousCluster) {
        cluster.geometry.coordinates[0] = cluster.geometry.coordinates[0] + index * 0.00018;
      }
      return cluster;
    });
    return newClu;
  }
  return superClusterClusters;
}

export type LocationOverlap = {
  hasOverlap: boolean;
  overlapCount: number;
};

/**
 * Resolves overlap count for a coordinate key; `?? 0` when the key was not counted (defensive).
 * Exported for unit tests so both branches of the nullish lookup are covered.
 */
export const getCoordinateOverlapCount = (
  coordinatesCount: Record<string, number>,
  coordinateKey: string
): number => coordinatesCount[coordinateKey] ?? 0;

export const getLocationOverlapMetadata = (
  mapMarkerLocations: MapMarkerLocation[] | undefined
): Record<number, LocationOverlap> => {
  if (!mapMarkerLocations?.length) {
    return {};
  }

  const coordinatesCount = mapMarkerLocations.reduce<Record<string, number>>((acc, location) => {
    const latitude = location.geoInformation?.[0]?.latitude;
    const longitude = location.geoInformation?.[0]?.longitude;
    if (latitude == null || longitude == null) {
      return acc;
    }

    const coordinateKey = `${latitude}-${longitude}`;
    const currentCoordinateCount = acc[coordinateKey] ?? 0;
    acc[coordinateKey] = currentCoordinateCount + 1;
    return acc;
  }, {});

  return mapMarkerLocations.reduce<Record<number, LocationOverlap>>((acc, location) => {
    const latitude = location.geoInformation?.[0]?.latitude;
    const longitude = location.geoInformation?.[0]?.longitude;

    if (latitude == null || longitude == null) {
      acc[location.locationInventoryId] = { hasOverlap: false, overlapCount: 0 };
      return acc;
    }

    const coordinateKey = `${latitude}-${longitude}`;
    const overlapCount = getCoordinateOverlapCount(coordinatesCount, coordinateKey);
    acc[location.locationInventoryId] = { hasOverlap: overlapCount > 1, overlapCount };
    return acc;
  }, {});
};

/** Expanded width of the desktop permanent drawer; must match `ResponsiveDrawer`. */
export const RESPONSIVE_DRAWER_EXPANDED_WIDTH_PX = 250;

/** Extra viewport `left` past drawer + gutter; aligns with facility report controls. */
export const FACILITY_REPORT_LOCATION_AUTOCOMPLETE_EXTRA_LEFT_PX = 130;

/** Gap between the Google map top control cluster and the location autocomplete (px). */
export const FACILITY_REPORT_LOCATION_AUTOCOMPLETE_MAP_CONTROLS_GAP_PX = 8;

/**
 * Viewport `right` of the Google Maps map-type strip: menubar (`role="menubar"`) and legacy
 * `.gm-style-mtc` (not the camera control). Used for horizontal autocomplete `left` only.
 */
export function getGoogleMapTopLeftControlsLayoutPx(
  doc: Pick<Document, "querySelector"> = document
): { right: number | null } {
  const mapRoot = doc.querySelector(".gm-style");
  if (!mapRoot) {
    return { right: null };
  }

  const rightEdges: number[] = [];

  const menubar = mapRoot.querySelector('[role="menubar"]');
  if (menubar instanceof Element) {
    const rect = menubar.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      rightEdges.push(rect.right);
    }
  }

  const legacyMapType = mapRoot.querySelector(".gm-style-mtc");
  if (legacyMapType instanceof Element) {
    const rect = legacyMapType.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      rightEdges.push(rect.right);
    }
  }

  if (rightEdges.length === 0) {
    return { right: null };
  }

  return {
    right: Math.max(...rightEdges)
  };
}

/** Right edge of the map-type strip; see {@link getGoogleMapTopLeftControlsLayoutPx}. */
export function getGoogleMapTopLeftControlsRightPx(
  doc: Pick<Document, "querySelector"> = document
): number | null {
  return getGoogleMapTopLeftControlsLayoutPx(doc).right;
}

type DocumentWithVendorFullscreen = Document & {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
};

function getFullscreenElementNode(doc: Document): Element | null {
  const d = doc as DocumentWithVendorFullscreen;
  return (
    doc.fullscreenElement ??
    d.webkitFullscreenElement ??
    d.mozFullScreenElement ??
    d.msFullscreenElement ??
    null
  );
}

/**
 * When the Google map is in browser fullscreen, overlays must mount under the same
 * fullscreen element or they are hidden. Returns that element when it is (or contains /
 * is contained by) `.gm-style`, otherwise `null`.
 */
export function getGoogleMapFullscreenPortalRoot(doc: Document): Element | null {
  const fs = getFullscreenElementNode(doc);
  if (!fs) {
    return null;
  }

  const gmStyle = doc.querySelector(".gm-style");
  if (!gmStyle) {
    return null;
  }

  if (fs.contains(gmStyle) || gmStyle.contains(fs)) {
    return fs;
  }

  return null;
}

/** Above map tiles in browser fullscreen; below max 32-bit signed int for portability. */
export const FACILITY_REPORT_MAP_LOCATION_AUTOCOMPLETE_FULLSCREEN_Z_INDEX = 2_147_000_000;

/**
 * `position: fixed` vertical offset (px) below `env(safe-area-inset-top)` on mobile — paired with
 * `calc(env(safe-area-inset-top) + …)` in the component. Tuned for app and map top chrome.
 */
export const FACILITY_REPORT_LOCATION_AUTOCOMPLETE_TOP_NORMAL_MOBILE_PX = 115;
export const FACILITY_REPORT_LOCATION_AUTOCOMPLETE_TOP_NORMAL_DESKTOP_PX = 75;

/**
 * `top` when the map is in browser fullscreen: sit just under the top edge so the field
 * stays clear of the map control strip but reads as “at the top” of the fullscreen view.
 */
export const FACILITY_REPORT_LOCATION_AUTOCOMPLETE_TOP_FULLSCREEN_MOBILE_PX = 16;
export const FACILITY_REPORT_LOCATION_AUTOCOMPLETE_TOP_FULLSCREEN_DESKTOP_PX = 12;

export function getFacilityReportLocationAutocompleteTopPx(args: {
  isMobile: boolean;
  isMapBrowserFullscreen: boolean;
}): number {
  if (args.isMapBrowserFullscreen) {
    return args.isMobile
      ? FACILITY_REPORT_LOCATION_AUTOCOMPLETE_TOP_FULLSCREEN_MOBILE_PX
      : FACILITY_REPORT_LOCATION_AUTOCOMPLETE_TOP_FULLSCREEN_DESKTOP_PX;
  }

  return args.isMobile
    ? FACILITY_REPORT_LOCATION_AUTOCOMPLETE_TOP_NORMAL_MOBILE_PX
    : FACILITY_REPORT_LOCATION_AUTOCOMPLETE_TOP_NORMAL_DESKTOP_PX;
}

/** Minimum `left` in map browser fullscreen when map-type `right` cannot be measured. */
export const FACILITY_REPORT_LOCATION_AUTOCOMPLETE_LEFT_FULLSCREEN_FALLBACK_MOBILE_PX = 10;
export const FACILITY_REPORT_LOCATION_AUTOCOMPLETE_LEFT_FULLSCREEN_FALLBACK_DESKTOP_PX = 8;

/**
 * Horizontal `left` for the fixed map location autocomplete: past map menubar when
 * measurable; otherwise drawer-based fallback in normal layout, or a small inset in
 * browser fullscreen (drawer is not shown).
 */
export function getFacilityReportLocationAutocompleteLeftPx(args: {
  mapTopControlsRightPx: number | null;
  controlsGapPx: number;
  normalFallbackLeftPx: number;
  isMobile: boolean;
  isMapBrowserFullscreen: boolean;
}): number {
  const fullscreenFallbackLeftPx = args.isMobile
    ? FACILITY_REPORT_LOCATION_AUTOCOMPLETE_LEFT_FULLSCREEN_FALLBACK_MOBILE_PX
    : FACILITY_REPORT_LOCATION_AUTOCOMPLETE_LEFT_FULLSCREEN_FALLBACK_DESKTOP_PX;

  if (args.isMapBrowserFullscreen) {
    if (args.mapTopControlsRightPx != null) {
      return Math.max(args.mapTopControlsRightPx + args.controlsGapPx, fullscreenFallbackLeftPx);
    }

    return fullscreenFallbackLeftPx;
  }

  if (args.mapTopControlsRightPx != null) {
    return Math.max(args.mapTopControlsRightPx + args.controlsGapPx, args.normalFallbackLeftPx);
  }

  return args.normalFallbackLeftPx;
}

/**
 * Horizontal `left` (viewport px) for the map location autocomplete on sm+ when using
 * `position: fixed`: drawer width + gutter + any extra offset past the drawer.
 */
export function getMapLocationAutocompleteLeftDesktopPx(args: {
  isDesktopDrawerExpanded: boolean;
  /** From `theme.spacing(7)` (collapsed mini drawer width). */
  spacing7: string;
  /** From `theme.spacing(2)` (gutter after drawer edge). */
  spacing2: string;
  /** Additional px past drawer + gutter (e.g. alignment with facility controls). */
  extraOffsetPx: number;
}): number {
  const drawerWidthPx = args.isDesktopDrawerExpanded
    ? RESPONSIVE_DRAWER_EXPANDED_WIDTH_PX
    : Number.parseFloat(args.spacing7);

  return drawerWidthPx + Number.parseFloat(args.spacing2) + args.extraOffsetPx;
}
